import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Avatar,
  Stack,
  Button,
  CircularProgress,
} from "@mui/material";
import { ArrowBackIosNew, Notifications, DoneAll } from "@mui/icons-material";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  updateDoc,
  limit,
  serverTimestamp,
  increment,
  arrayUnion,
  getDoc,
  writeBatch,
} from "firebase/firestore";
import { auth, db } from "./firebase";
import { useTranslatedDictionary } from "./hooks/useTranslatedDictionary";

const NOTIFICATION_TYPE_CATEGORY = {
  chat: "messages",
  post: "posts",
  profile: "followers",
  follow_request: "followers",
  follow_accepted: "followers",
};

const NOTIF_TEXT = {
  es: {
    title: "Notificaciones",
    marking: "Marcando...",
    markAll: "Marcar todas",
    emptyTitle: "No tienes notificaciones",
    emptySubtitle: "Aquí verás mensajes y publicaciones de las personas que sigues.",
    defaultTitle: "Nueva actividad",
    defaultBody: "Tienes actividad nueva",
    accept: "Aceptar",
    acceptAndFollowBack: "Aceptar y seguir también",
    reject: "Rechazar",
    followRequestError: "Hubo un error al procesar la solicitud. Por favor, inténtalo de nuevo.",
    followRequestExpired: "Esta solicitud ya no está pendiente (fue cancelada o ya respondida).",
    now: "Ahora",
  },
  en: {
    title: "Notifications",
    marking: "Marking...",
    markAll: "Mark all",
    emptyTitle: "You have no notifications",
    emptySubtitle: "You'll see messages and posts from people you follow here.",
    defaultTitle: "New activity",
    defaultBody: "You have new activity",
    accept: "Accept",
    acceptAndFollowBack: "Accept and follow back",
    reject: "Decline",
    followRequestError: "There was an error processing the request. Please try again.",
    followRequestExpired: "This request is no longer pending (it was cancelled or already answered).",
    now: "Now",
  },
};

const formatTime = (timestamp, t) => {
  const date = timestamp?.toDate ? timestamp.toDate() : null;
  if (!date) return t("now");
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const Notificaciones = ({ onBack, onOpenChat, onOpenPost, onOpenProfile, language = "es", themeMode = "oscuro" }) => {
  const t = useTranslatedDictionary(NOTIF_TEXT, language);
  const isLightTheme = themeMode === "claro";
  const onSurface = (opacity = 1) =>
    isLightTheme ? `rgba(15,20,32,${opacity})` : `rgba(255,255,255,${opacity})`;
  const currentUser = auth.currentUser;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);
  const [actioningId, setActioningId] = useState("");
  const [notificationPrefs, setNotificationPrefs] = useState({ followers: true, messages: true, posts: true });

  useEffect(() => {
    if (!currentUser) return;
    const unsubscribe = onSnapshot(doc(db, "users", currentUser.uid), (snap) => {
      const prefs = snap.exists() ? snap.data()?.notificationPrefs : null;
      if (prefs) setNotificationPrefs((prev) => ({ ...prev, ...prefs }));
    });
    return () => unsubscribe();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) {
      setItems([]);
      setLoading(false);
      return;
    }

    const notificationsQuery = query(
      collection(db, "users", currentUser.uid, "notifications"),
      orderBy("createdAt", "desc"),
      limit(100)
    );

    const unsubscribe = onSnapshot(
      notificationsQuery,
      (snapshot) => {
        setItems(snapshot.docs.map((docItem) => ({ id: docItem.id, ...docItem.data() })));
        setLoading(false);
      },
      () => {
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [currentUser]);

  const unreadItems = useMemo(() => items.filter((item) => !item.read), [items]);

  const visibleItems = useMemo(() => {
    return items.filter((item) => {
      const category = NOTIFICATION_TYPE_CATEGORY[item.type];
      if (!category) return true;
      return notificationPrefs[category] !== false;
    });
  }, [items, notificationPrefs]);

  const markAsRead = async (id) => {
    if (!currentUser || !id) return;
    await updateDoc(doc(db, "users", currentUser.uid, "notifications", id), {
      read: true,
      readAt: serverTimestamp(),
    });
  };

  const markAllAsRead = async () => {
    if (!currentUser || unreadItems.length === 0 || markingAll) return;
    setMarkingAll(true);
    try {
      await Promise.all(
        unreadItems.map((item) =>
          updateDoc(doc(db, "users", currentUser.uid, "notifications", item.id), {
            read: true,
            readAt: serverTimestamp(),
          })
        )
      );
    } finally {
      setMarkingAll(false);
    }
  };

  const handleFollowRequestAction = async (item, action) => {
    if (!currentUser || !item || actioningId) return;
    const requesterUid = item.actorUid || item.authorUid;
    if (!requesterUid) return;

    setActioningId(item.id);
    try {
      const requesterRef = doc(db, "users", requesterUid);
      const currentRef = doc(db, "users", currentUser.uid);
      const followRequestId = `follow_request_${requesterUid}_${currentUser.uid}`;
      const followRequestRef = doc(db, "follow_requests", followRequestId);

      // Todas las notificaciones pendientes del mismo solicitante se resuelven juntas
      const relatedNotifications = items.filter(
        (n) =>
          n.type === "follow_request" &&
          (n.actorUid || n.authorUid) === requesterUid &&
          (n.status || "pending") === "pending"
      );
      if (!relatedNotifications.some((n) => n.id === item.id)) relatedNotifications.push(item);

      const resolveNotifications = (batch, status) => {
        relatedNotifications.forEach((n) =>
          batch.update(doc(db, "users", currentUser.uid, "notifications", n.id), {
            status,
            read: true,
            respondedAt: serverTimestamp(),
          })
        );
      };

      // Las reglas solo permiten modificar al solicitante si la solicitud sigue pendiente
      const requestSnap = await getDoc(followRequestRef);
      const requestStatus = requestSnap.exists() ? requestSnap.data().status : null;
      if (requestStatus !== "pending") {
        const batch = writeBatch(db);
        resolveNotifications(batch, requestStatus || "expired");
        await batch.commit();
        alert(t("followRequestExpired"));
        return;
      }

      const batch = writeBatch(db);

      if (action === "accept" || action === "accept_follow_back") {
        const followBack = action === "accept_follow_back";

        // Los contadores de seguidores los recalcula el backend a partir de "following".
        batch.update(requesterRef, {
          following: arrayUnion(currentUser.uid),
          followingCount: increment(1),
        });
        if (followBack) {
          batch.update(currentRef, {
            following: arrayUnion(requesterUid),
            followingCount: increment(1),
          });
        }

        batch.set(
          followRequestRef,
          {
            status: followBack ? "accepted_follow_back" : "accepted",
            updatedAt: serverTimestamp(),
            respondedAt: serverTimestamp(),
          },
          { merge: true }
        );

        resolveNotifications(batch, action);

        const actorName = currentUser.displayName || currentUser.email?.split("@")[0] || "Usuario";
        batch.set(
          doc(db, "users", requesterUid, "notifications", `follow_accepted_${currentUser.uid}_${Date.now()}`),
          {
            type: "follow_accepted",
            authorUid: currentUser.uid,
            actorUid: currentUser.uid,
            title: followBack ? "Aceptaron tu solicitud y te siguieron también" : "Aceptaron tu solicitud de seguimiento",
            body: followBack
              ? `${actorName} aceptó tu solicitud y también te sigue.`
              : `${actorName} aceptó tu solicitud de seguimiento.`,
            actorPhotoURL: currentUser.photoURL || "",
            read: false,
            createdAt: serverTimestamp(),
          }
        );
      } else if (action === "reject") {
        batch.set(
          followRequestRef,
          {
            status: "rejected",
            updatedAt: serverTimestamp(),
            respondedAt: serverTimestamp(),
          },
          { merge: true }
        );
        resolveNotifications(batch, "rejected");
      }

      await batch.commit();
    } catch (error) {
      console.error("Error al procesar la solicitud de seguimiento:", error);
      alert(t("followRequestError"));
    } finally {
      setActioningId("");
    }
  };

  const openNotification = async (item) => {
    if (!item) return;

    if (!item.read) {
      try {
        await markAsRead(item.id);
      } catch {
        // No bloquea la navegacion.
      }
    }

    if (item.type === "chat" && item.chatId && typeof onOpenChat === "function") {
      onOpenChat(item.chatId);
      return;
    }

    if (item.type === "post" && item.postId && typeof onOpenPost === "function") {
      onOpenPost(item.postId);
      return;
    }

    if (item.authorUid && typeof onOpenProfile === "function") {
      onOpenProfile(item.authorUid);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        color: onSurface(1),
        background: isLightTheme ? "linear-gradient(to bottom, #ffffff, #f2f5f8)" : "linear-gradient(to bottom, #101010, #000)",
        p: 2,
        pt: 1,
        pb: 10,
        maxWidth: "860px",
        mx: "auto",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 2.5, gap: 1.5 }}>
        <IconButton onClick={onBack} sx={{ color: onSurface(1) }}>
          <ArrowBackIosNew fontSize="small" />
        </IconButton>
        <Typography variant="h6" sx={{ fontWeight: 800, flex: 1 }}>
          {t("title")}
        </Typography>
        <Button
          onClick={markAllAsRead}
          disabled={unreadItems.length === 0 || markingAll}
          startIcon={markingAll ? <CircularProgress size={18} sx={{ color: "#00d4ff" }} /> : <DoneAll sx={{ fontSize: 20 }} />}
          sx={{
            textTransform: "none",
            borderRadius: "12px",
            px: 2.2,
            py: 1.2,
            fontSize: "0.95rem",
            fontWeight: 600,
            color: onSurface(1),
            bgcolor: "rgba(0, 212, 255, 0.15)",
            border: "1.5px solid rgba(0, 213, 255, 0.7)",
            transition: "all 0.2s",
            "&:hover:not(:disabled)": {
              bgcolor: "rgba(0, 212, 255, 0.25)",
              border: "1.5px solid #00d4ff",
              boxShadow: "0 0 12px rgba(0, 212, 255, 0.3)",
            },
            "&.Mui-disabled": {
              color: onSurface(0.55),
              bgcolor: onSurface(0.06),
              border: `1.5px solid ${onSurface(0.16)}`,
              cursor: "not-allowed",
            },
          }}
        >
          {markingAll ? t("marking") : t("markAll")}
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress sx={{ color: "#00d4ff" }} />
        </Box>
      ) : visibleItems.length === 0 ? (
        <Paper
          sx={{
            p: 4,
            bgcolor: onSurface(isLightTheme ? 0.04 : 0.05),
            borderRadius: "16px",
            border: `1px solid ${onSurface(0.08)}`,
            textAlign: "center",
          }}
        >
          <Notifications sx={{ fontSize: 42, color: onSurface(0.3), mb: 1 }} />
          <Typography variant="body1" sx={{ fontWeight: 600 }}>
            {t("emptyTitle")}
          </Typography>
          <Typography variant="body2" sx={{ color: onSurface(0.55) }}>
            {t("emptySubtitle")}
          </Typography>
        </Paper>
      ) : (
        <Stack spacing={1.2}>
          {visibleItems.map((item) => {
            const title = item.title || t("defaultTitle");
            const body = item.body || t("defaultBody");
            const avatarSrc = item.actorPhotoURL || item.requesterPhotoURL || "";
            const avatarFallbackText =
              (item.title || item.body || item.authorUid || "U").trim().charAt(0).toUpperCase() || "U";
            const isFollowRequest = item.type === "follow_request" && (item.status || "pending") === "pending";

            return (
              <Paper
                key={item.id}
                onClick={() => openNotification(item)}
                sx={{
                  p: 1.4,
                  display: "flex",
                  gap: 1.1,
                  alignItems: "center",
                  cursor: "pointer",
                  bgcolor: item.read ? onSurface(0.04) : "rgba(0,212,255,0.10)",
                  borderRadius: "14px",
                  border: item.read
                    ? `1px solid ${onSurface(0.08)}`
                    : "1px solid rgba(0,212,255,0.4)",
                  transition: "all .2s",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    bgcolor: item.read ? onSurface(0.08) : "rgba(0,212,255,0.16)",
                  },
                }}
              >
                <Avatar src={avatarSrc || undefined} sx={{ width: 40, height: 40 }}>
                  {!avatarSrc ? avatarFallbackText : null}
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700, color: onSurface(1) }}>
                    {title}
                  </Typography>
                  <Typography variant="body2" sx={{ color: onSurface(0.8) }}>
                    {body}
                  </Typography>
                  <Typography variant="caption" sx={{ color: onSurface(0.5) }}>
                    {formatTime(item.createdAt, t)}
                  </Typography>
                  {isFollowRequest && (
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFollowRequestAction(item, "accept");
                        }}
                        disabled={actioningId === item.id}
                        sx={{
                          textTransform: "none",
                          borderRadius: "10px",
                          px: 1.4,
                          py: 0.6,
                          fontWeight: 700,
                          bgcolor: "rgba(0,212,255,0.18)",
                          color: "#00d4ff",
                        }}
                      >
                        {t("accept")}
                      </Button>
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFollowRequestAction(item, "accept_follow_back");
                        }}
                        disabled={actioningId === item.id}
                        sx={{
                          textTransform: "none",
                          borderRadius: "10px",
                          px: 1.4,
                          py: 0.6,
                          fontWeight: 700,
                          bgcolor: onSurface(0.1),
                          color: onSurface(1),
                        }}
                      >
                        {t("acceptAndFollowBack")}
                      </Button>
                      <Button
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleFollowRequestAction(item, "reject");
                        }}
                        disabled={actioningId === item.id}
                        sx={{
                          textTransform: "none",
                          borderRadius: "10px",
                          px: 1.4,
                          py: 0.6,
                          fontWeight: 700,
                          bgcolor: "rgba(255,77,77,0.14)",
                          color: "#ff6b6b",
                        }}
                      >
                        {t("reject")}
                      </Button>
                    </Stack>
                  )}
                </Box>
                {!item.read && (
                  <Box
                    sx={{
                      width: 9,
                      height: 9,
                      borderRadius: "50%",
                      bgcolor: "#00d4ff",
                      boxShadow: "0 0 0 4px rgba(0,212,255,0.18)",
                    }}
                  />
                )}
              </Paper>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default Notificaciones;
